// @givmo/board — EVERY word CBG-RC9-MULTI's screens add, in one place: one written consent for a
// meeting's minutes and its closed sessions' (the approved design's §2.6 item 3; backend contract
// `board-minutes-by-consent.md` §M1–§M17).
//
// ══ WHICH WORDS ARE APPROVED ════════════════════════════════════════════════════════════════════
//
// Every line below: approved in copy round CBG-RC9-MULTI (Jonathan, 2026-10-14 19:32 PDT, "go with
// your recs"), as the cut's copy page shows it
// (`scratchpad/2026-10-14-rc9multi-copy-round/copy-review.html`).
// Each names the frame that draws it (`m2`, `m3`, `c1`, `d4`, `d4b`; the design branch
// `design/board-minutes-approval-closed-sessions` @ `8d5c8ee8`), or "not drawn" for a state no frame
// shows. Every approved line they sit beside is reused from where it lives (RC9's
// `minutes-consent-words.ts`, RC11's `minutes-rule-words.ts`), never copied.
//
// A consent naming ONE set says exactly what RC9 and RC11 said: these lines are reached only when a
// consent names two or more sets of one meeting's minutes, or a page names the others beside its own.
//
// ⚠ Plan §3.2 and §3.4 bind every line: what Board recorded and what a signature does, never a
// conclusion about the minutes. A closed session is named by its general purpose only, never its
// title (RC11's §A7). No pronoun for a named person.

import { minutesOfTheMeeting, theMinutesOf, type MinutesNaming } from './minutes-consent-words';
import { closedMinutesOfTheMeeting, theClosedMinutesOf } from '../minutes/minutes-rule-words';

/** One set of minutes as these lines name it: the meeting's own, or a closed session's by purpose. */
export interface SetNaming {
  /** Null for the meeting's own minutes; a closed session's general purpose (or null) for its own. */
  closedSession: { purposeLabel: string | null } | null;
}

/** The meeting the sets are of: its title as the organization wrote it, and its date. */
export interface SetsMeeting {
  title: string | null | undefined;
  date: string | null | undefined;
}

function purposeOf(set: SetNaming): string {
  return (set.closedSession?.purposeLabel ?? '').trim();
}

function closedCount(sets: readonly SetNaming[]): number {
  return sets.filter((set) => set.closedSession !== null).length;
}

/** "A, and B" / "A, B, and C": the serial comma, as frame `d4`'s lead and the consent's RESOLVED. */
function serialSeries(items: readonly string[]): string {
  if (items.length <= 1) return items[0] ?? '';
  return `${items.slice(0, -1).join(', ')}, and ${items[items.length - 1]}`;
}

/** "A and B" / "A, B and C": a page's own line, as frame `m3`'s. */
function plainSeries(items: readonly string[]): string {
  if (items.length <= 1) return items[0] ?? '';
  return `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}`;
}

/**
 * A closed set named beside the meeting's own minutes, in a sentence: "the minutes of its closed
 * session (Compensation)". Drawn, d4 and d4b.
 */
function itsClosedMinutes(set: SetNaming): string {
  const purpose = purposeOf(set);
  return purpose.length > 0
    ? `the minutes of its closed session (${purpose})`
    : // Not drawn: no general purpose recorded.
      'the minutes of its closed session';
}

/**
 * Every set a consent names, in a sentence and in its order: "the minutes of the January board
 * meeting of January 28, 2027, and the minutes of its closed session (Compensation)". The first set is
 * named as RC9 and RC11 name one (approved); a closed set after the meeting's own minutes is "the
 * minutes of its closed session (…)" (drawn, d4); after a closed first set, each is named in full
 * by RC11's approved form, since "its" would then have nothing to point at. One set: RC9's or RC11's
 * naming alone, unchanged.
 */
export function setsInASentence(
  sets: readonly SetNaming[],
  meeting: SetsMeeting,
  naming: MinutesNaming = {},
): string {
  const [first, ...later] = sets;
  if (first === undefined) return minutesOfTheMeeting(meeting.title, meeting.date, naming);
  const inFull = (set: SetNaming) =>
    set.closedSession === null
      ? minutesOfTheMeeting(meeting.title, meeting.date, naming)
      : closedMinutesOfTheMeeting(
          meeting.title,
          meeting.date,
          set.closedSession.purposeLabel,
          naming,
        );
  const firstIsTheMeetings = first.closedSession === null;
  return serialSeries([
    inFull(first),
    ...later.map((set) => (firstIsTheMeetings ? itsClosedMinutes(set) : inFull(set))),
  ]);
}

/**
 * Each set on its own row, as the dialog's "Approves" lists them: "The minutes of the January board
 * meeting, January 28, 2027" over "The minutes of its closed session (Compensation)". The first row is
 * RC9's or RC11's approved naming; a closed set after the meeting's own minutes is drawn (m2);
 * after a closed first set, RC11's approved form in full.
 */
export function setsAsRows(
  sets: readonly SetNaming[],
  meeting: SetsMeeting,
  naming: MinutesNaming = {},
): string[] {
  const [first] = sets;
  const firstIsTheMeetings = first?.closedSession === null;
  return sets.map((set, index) => {
    if (set.closedSession === null) return theMinutesOf(meeting.title, meeting.date, naming);
    if (index > 0 && firstIsTheMeetings) {
      const purpose = purposeOf(set);
      // Drawn, m2; not drawn, with no general purpose recorded.
      return purpose.length > 0
        ? `The minutes of its closed session (${purpose})`
        : 'The minutes of its closed session';
    }
    return theClosedMinutesOf(meeting.title, meeting.date, set.closedSession.purposeLabel, naming);
  });
}

/**
 * One set named on its own at the start of a sentence, where the lead above it has named every set in
 * full: "The meeting’s minutes", "The closed session’s minutes", or, with two closed sessions named,
 * "The minutes of the closed session (Compensation)". Not drawn.
 */
export function oneSetShortly(set: SetNaming, sets: readonly SetNaming[]): string {
  if (set.closedSession === null) return 'The meeting’s minutes';
  if (closedCount(sets) <= 1) return 'The closed session’s minutes';
  const purpose = purposeOf(set);
  return purpose.length > 0
    ? `The minutes of the closed session (${purpose})`
    : 'The minutes of a closed session';
}

/** The same, inside a sentence. */
function oneSetShortlyInASentence(set: SetNaming, sets: readonly SetNaming[]): string {
  const named = oneSetShortly(set, sets);
  return named.charAt(0).toLowerCase() + named.slice(1);
}

/**
 * The other sets a consent names beside the page's own, said on the page's line: on the meeting's own
 * minutes, "the minutes of their closed session"; on a closed session's, "the meeting’s own minutes",
 * and any other closed session's. `others` is the read's `also_names` (or `consent_names_with`).
 */
function theOtherSets(others: readonly SetNaming[], onAClosedSession: boolean): string[] {
  const closed = closedCount(others);
  if (!onAClosedSession) {
    // Drawn, m3 (one closed session); not drawn, several.
    return closed === 0
      ? []
      : [
          closed === 1
            ? 'the minutes of their closed session'
            : 'the minutes of their closed sessions',
        ];
  }
  const parts: string[] = [];
  const theMeetings = others.some((set) => set.closedSession === null);
  // Drawn, c1; not drawn, the line while out.
  if (theMeetings) parts.push('the meeting’s own minutes');
  if (closed > 0) {
    // Not drawn: a meeting with two or more closed sessions.
    parts.push(
      theMeetings
        ? `the minutes of its other closed session${closed === 1 ? '' : 's'}`
        : `the minutes of the meeting’s other closed session${closed === 1 ? '' : 's'}`,
    );
  }
  return parts;
}

export const minutesSetsWords = {
  // ── Starting the consent (the dialog over the minutes) ──────────────────────────────────────────

  // Drawn, m2 (two sets); not drawn, three or more. RC9's approved lead with
  // the sets counted.
  dialogLead: (count: number) =>
    count === 2
      ? 'Start a written consent for your directors to sign. When the last director signs, both sets of minutes are approved, as they are when the consent goes out.'
      : 'Start a written consent for your directors to sign. When the last director signs, each set of minutes is approved, as it is when the consent goes out.',
  // Not drawn. RC11's approved closed note (`consentClosedNote`) says "It
  // names these minutes by the closed session’s general purpose only", which would misname the
  // meeting's own minutes here, so a consent naming several sets says which minutes it means.
  // CBG-FIX104 (row 3.12, approved 2026-10-17 17:42 PDT): who reads it, by the minutes' body; the
  // forms for several sets keep their own endings.
  closedNote: (closedSessions: number, committeeMinutes = false) =>
    `${
      committeeMinutes
        ? 'Everyone on the committee reads this consent.'
        : 'Everyone serving on the board reads this consent, including officers who aren’t directors.'
    } ${
      closedSessions > 1
        ? 'It names each closed session’s minutes by the session’s general purpose only.'
        : 'It names the closed session’s minutes by the session’s general purpose only.'
    }`,
  /**
   * Not drawn. RC9's `filledInForYou` for several sets, saying what the form
   * fills in as the served provenance says it (backend `several_minutes_provenance`): with the
   * meeting's own minutes named, their title and date; with closed sessions' alone, the meeting's date.
   */
  filledIn: (input: { meetingMinutesNamed: boolean; closedSessions: number }) => {
    const which = input.meetingMinutesNamed
      ? `the minutes’ title and date, ${input.closedSessions > 1 ? 'each closed session’s' : 'the closed session’s'} general purpose, and each set’s record and fingerprint`
      : 'the meeting’s date, each session’s general purpose, and each set’s record and fingerprint';
    return `Givmo fills in which minutes, from your records: ${which}. Keep the fingerprints as they are — they’re how the signatures name these exact minutes, and the consent can’t go out to the board without them. The terms in brackets are yours to fill in.`;
  },

  // ── The minutes page ────────────────────────────────────────────────────────────────────────────

  /**
   * RC9's pending line naming the other sets the consent out names: "A written consent approving these
   * minutes and the minutes of their closed session is out for signature." Drawn, m3; not drawn, on
   * a closed session's own minutes and with several closed sessions.
   */
  pendingLine: (others: readonly SetNaming[], onAClosedSession: boolean) =>
    `A written consent approving ${plainSeries(['these minutes', ...theOtherSets(others, onAClosedSession)])} is out for signature.`,
  /**
   * The closed session's way line while "Approve by written consent" would name the meeting's own
   * minutes with them: "These minutes are approved with the meeting’s own minutes, by one written
   * consent of the directors." Drawn, c1; not drawn, with other closed
   * sessions too.
   */
  approvedWithLine: (others: readonly SetNaming[]) =>
    `These minutes are approved with ${plainSeries(theOtherSets(others, true))}, by one written consent of the directors.`,

  // ── Putting it to the board (the circulate dialog) ──────────────────────────────────────────────

  // Not drawn. RC9's approved `circulateLead`, each set named by its own
  // fingerprint.
  circulateLead: (sets: string) =>
    `Signing it approves ${sets}, each named in its text by its fingerprint.`,
  // Not drawn. RC9's approved `circulateIfChanged` for several sets, each of
  // which the last signature decides on its own.
  circulateIfChanged:
    'Each set is approved as it is now. If one changes before the last signature, the consent is still filed, and that set isn’t approved.',

  // ── The director's page ─────────────────────────────────────────────────────────────────────────

  /**
   * A closed session's own minutes, opened from a consent naming two or more closed sessions' minutes,
   * where RC11's approved "Read the closed session’s minutes" can't say which. Not drawn.
   */
  readClosedMinutesOf: (set: SetNaming) => {
    const purpose = purposeOf(set);
    return purpose.length > 0
      ? `Read the minutes of the closed session (${purpose})`
      : 'Read the minutes of a closed session';
  },
  /**
   * RC9's would-not-approve line for one set of several, led by that set (the lead above names every
   * set in full). Not drawn; RC9's reasons, worded per set.
   */
  wontApprove: (set: SetNaming, sets: readonly SetNaming[], reason: string | null) => {
    const named = oneSetShortly(set, sets);
    switch (reason) {
      case 'minutes_changed':
        return `${named} have changed since this consent went out, so it won’t approve them.`;
      case 'minutes_already_approved':
        return `${named} have been approved since this consent went out, so signing it won’t approve them again.`;
      case 'meeting_record_changed':
        return `The meeting’s record has changed since ${oneSetShortlyInASentence(set, sets)} were drafted, so this consent won’t approve them.`;
      case 'minutes_unavailable':
        return `${named} can no longer be approved, so this consent won’t approve them.`;
      default:
        return `This consent won’t approve ${oneSetShortlyInASentence(set, sets)}.`;
    }
  },

  // ── The consent's own page (officer) ────────────────────────────────────────────────────────────

  /**
   * A closed set's served name on the officer's page ("Closed session minutes — January board
   * meeting") with its general purpose, where two closed sessions would otherwise read the same. Not
   * new words: the served name and the purpose the organization recorded.
   */
  servedNameWithPurpose: (served: string, set: SetNaming) => {
    const purpose = purposeOf(set);
    return purpose.length > 0 ? `${served} (${purpose})` : served;
  },
} as const;

/** How many of the sets are closed sessions' own minutes. */
export function closedSessionsAmong(sets: readonly SetNaming[]): number {
  return closedCount(sets);
}
